import { type ReactNode, useRef } from 'react';
import CheckIcon from '@/assets/icons/check.svg?react';
import { LINKS } from '@/shared/config/links';
import { useEntranceMotion } from '@/shared/hooks/use-entrance-motion';
import { Wordmark } from '@/shared/ui/wordmark';
import { BrandPods } from '../brand-art';

const FACTS = [
  'Mac Pods with the Latest Xcode and Simulators already booted',
  'Claude Code and Codex start the sessions, Macswarm hands out the Pods',
  '3 Pod-hours free, no card. Then Pro from $99 a month',
] as const;

type AuthLayoutProps = {
  /** Changes when the pane shows a new view, so the entrance replays. */
  viewKey: string;
  children: ReactNode;
};

/** Sign in and welcome: the amber brand field on the left, one task on the right. */
export function AuthLayout({ viewKey, children }: AuthLayoutProps) {
  const brandRef = useRef<HTMLElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  useEntranceMotion(brandRef, 'brand', { floatPods: true });
  useEntranceMotion(paneRef, viewKey);

  return (
    <div className="flex min-h-screen flex-wrap bg-bg text-body-lg text-ink">
      <section
        ref={brandRef}
        data-on-field=""
        className="relative flex shrink grow basis-130 flex-col gap-8 overflow-hidden bg-field px-5 py-7 text-field-ink split:min-h-screen split:px-14 split:py-10"
      >
        <BrandPods variant="auth" className="-right-7.5 top-10 h-75 w-105" />
        <a href={LINKS.HOME} className="relative self-start">
          <Wordmark size="lg" />
        </a>
        <div className="relative mt-auto flex max-w-130 flex-col gap-7">
          <h2 className="font-semibold text-brand">Run a swarm of coding agents on real Macs.</h2>
          <ul className="hidden flex-col gap-3.5 text-fact split:flex">
            {FACTS.map((fact) => (
              <li key={fact} className="grid grid-cols-[22px_1fr] items-start gap-3">
                <CheckIcon className="size-5.5" />
                <span>{fact}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative hidden max-w-130 text-field-muted text-meta split:block">
          Mac, macOS, Xcode, and iOS Simulator are trademarks of Apple Inc. Macswarm is not
          affiliated with or endorsed by Apple.
        </p>
      </section>
      <main className="flex shrink grow basis-110 items-center justify-center px-5 pt-8 pb-12 split:px-8 split:py-12">
        <div ref={paneRef} className="flex w-full max-w-95 flex-col gap-5.5">
          {children}
        </div>
      </main>
    </div>
  );
}
